import { CheckIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, PlayIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import type { User } from '../../../@types/User';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import type { NextAction, TeamAction } from '../utils/nextActions';
import { formatActionDate } from '../utils/nextActions';

interface NextActionsSectionProps {
  actions: NextAction[];
  userName?: string;
  companyUsers?: User[];
  teamActions?: TeamAction[];
  onMarkTeamActionDone?: (action: TeamAction) => Promise<void>;
}

function getUserName(userId: number | null, companyUsers: User[]): string | null {
  if (!userId) { return null; }
  const user = companyUsers.find((u) => u.id === userId);
  if (!user) { return null; }
  return `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || null;
}

function getTeamUserName(userId: number, companyUsers: User[]): string {
  const user = companyUsers.find((u) => u.id === userId);
  if (!user) { return `Utilisateur #${userId}`; }
  return `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || `Utilisateur #${userId}`;
}

function ActionCard({ action, companyUsers }: { action: NextAction; companyUsers: User[] }) {
  const assignedName = getUserName(action.assignedUserId, companyUsers);

  return (
    <div className="flex items-stretch overflow-hidden rounded-[10px] border border-gray-200 bg-white shadow-sm">
      {/* Date column */}
      <div className="flex w-[130px] shrink-0 flex-col items-center justify-center gap-1 border-r-[3px] border-r-rgpd-blue bg-blue-50 px-2.5 py-3">
        {action.frequencyLabel ? (
          <span className="text-center text-[13px] leading-tight font-extrabold text-rgpd-blue-dark">
            {action.frequencyLabel}
          </span>
        ) : action.date ? (
          <span className="text-center text-[13px] font-extrabold tracking-wide text-rgpd-blue-dark">
            {formatActionDate(action.date)}
          </span>
        ) : (
          <span className="text-center text-[11px] font-bold text-gray-400">
            À planifier
          </span>
        )}
      </div>

      {/* Info column */}
      <div className="flex flex-1 flex-col justify-center px-4 py-3">
        <div className="mb-1 text-[13px] font-extrabold text-slate-900">
          {action.title}
        </div>
        <div className="text-xs leading-relaxed text-gray-600">
          {action.description}
        </div>
        {action.warning && (
          <div className="mt-1.5 flex items-center gap-1 text-xs font-bold text-red-600">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
            {action.warning}
          </div>
        )}
      </div>

      {/* Right column: assigned user + CTA */}
      <div className="flex shrink-0 items-center gap-3.5 px-4 py-3">
        {assignedName && (
          <span className="text-xs font-bold whitespace-nowrap text-gray-600">
            {assignedName}
          </span>
        )}
        <Link
          to={action.route}
          className="inline-flex items-center gap-1.5 rounded-[7px] bg-rgpd-blue-dark px-3.5 py-2 text-[11px] font-bold whitespace-nowrap text-white transition-opacity hover:opacity-85"
        >
          Accéder
          <ChevronRightIcon className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: TeamAction['status'] }) {
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-extrabold text-red-600">
        <span role="img" aria-label="warning">⚠️</span> À relancer
      </span>
    );
  }
  return (
    <span className="inline-flex animate-pulse items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-extrabold text-amber-700">
      &#9203; À faire
    </span>
  );
}

interface TeamTrackingSubSectionProps {
  actions: TeamAction[];
  companyUsers: User[];
  onMarkDone?: (action: TeamAction) => Promise<void>;
}

function TeamTrackingSubSection({ actions, companyUsers, onMarkDone }: TeamTrackingSubSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [actionToMarkDone, setActionToMarkDone] = useState<TeamAction | null>(null);
  const [isMarkingDone, setIsMarkingDone] = useState(false);

  const handleConfirmMarkDone = async() => {
    if (!actionToMarkDone || !onMarkDone) { return; }
    setIsMarkingDone(true);
    try {
      await onMarkDone(actionToMarkDone);
      setActionToMarkDone(null);
    } finally {
      setIsMarkingDone(false);
    }
  };

  return (
    <div className="mt-6">
      {/* Sub-section header */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="mb-3 flex w-full cursor-pointer items-center gap-2.5 text-left"
      >
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-rgpd-blue-dark transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
        />
        <span className="text-[15px] font-extrabold text-slate-900">
          Les actions que vous avez confiées à des collaborateurs
        </span>
        <span className="text-[13px] font-bold text-gray-500">
          (À vérifier avec eux)
        </span>
      </button>

      {isOpen && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Column headers */}
          <div className="grid grid-cols-[130px_16px_2fr_1fr_1fr_180px] border-b-2 border-gray-200 bg-gray-50">
            <div className="border-r-2 border-gray-200 px-5 py-2.5 text-[10.5px] font-extrabold tracking-wider text-gray-600 uppercase">
              Date prévue
            </div>
            <div />
            <div className="px-5 py-2.5 text-[10.5px] font-extrabold tracking-wider text-gray-600 uppercase">
              Action
            </div>
            <div className="px-2 py-2.5 text-center text-[10.5px] font-extrabold tracking-wider text-gray-600 uppercase">
              Responsable
            </div>
            <div className="px-2 py-2.5 text-center text-[10.5px] font-extrabold tracking-wider text-gray-600 uppercase">
              Statut
            </div>
            <div className="px-5 py-2.5 text-right text-[10.5px] font-extrabold tracking-wider text-gray-600 uppercase">
              Action
            </div>
          </div>

          {/* Rows */}
          {actions.map((action, index) => (
            <div
              key={action.id}
              className={`grid grid-cols-[130px_16px_2fr_1fr_1fr_180px] items-center bg-white ${
                index < actions.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="flex h-full items-center self-stretch border-r-2 border-gray-200 px-4 py-3">
                <span className="text-xs font-extrabold text-rgpd-blue-dark">
                  {action.frequencyLabel
                    ? action.frequencyLabel
                    : action.date
                      ? `\uD83D\uDCC5 ${formatActionDate(action.date)}`
                      : 'À planifier'}
                </span>
              </div>
              <div />
              <div className="py-2">
                <div className="text-[13px] font-bold text-gray-900">{action.title}</div>
                <div className="mt-0.5 text-[11px] text-gray-500">{action.description}</div>
              </div>
              <div className="px-2 text-center text-xs font-semibold text-gray-600">
                {getTeamUserName(action.assignedUserId, companyUsers)}
              </div>
              <div className="px-2 text-center">
                <StatusBadge status={action.status} />
              </div>
              {/* Right-aligned so "Relancer" sits at the same place on every row. */}
              <div className="flex items-center justify-end gap-1.5 px-5">
                {/* Only a past, dated occurrence can be closed for the cycle. */}
                {onMarkDone && action.eventKey && action.status === 'overdue' && (
                  <button
                    type="button"
                    onClick={() => setActionToMarkDone(action)}
                    title="Marquer comme fait pour ce cycle"
                    aria-label={`Marquer comme fait : ${action.title}`}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-[5px] text-[10.5px] font-bold whitespace-nowrap text-gray-700 transition hover:border-gray-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-rgpd-blue-dark/30 focus-visible:outline-none active:scale-[0.98]"
                  >
                    <CheckIcon className="h-3 w-3 text-emerald-600" strokeWidth={2.5} />
                    Fait
                  </button>
                )}
                <Link
                  to={action.route}
                  className="inline-flex items-center gap-1 rounded-md bg-rgpd-blue-dark px-3 py-1.5 text-[10.5px] font-bold text-white transition-opacity hover:opacity-85"
                >
                  Relancer
                  <ChevronRightIcon className="h-3 w-3" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmationDialog
        open={actionToMarkDone !== null}
        title="Marquer comme fait"
        confirmLabel="Oui, c'est fait"
        onConfirm={handleConfirmMarkDone}
        onClose={() => { if (!isMarkingDone) { setActionToMarkDone(null); } }}
        loading={isMarkingDone}
        description={actionToMarkDone && (
          <p className="text-gray-700">
            L'échéance <strong>{actionToMarkDone.title}</strong>
            {actionToMarkDone.date && <> du <strong>{formatActionDate(actionToMarkDone.date)}</strong></>}
            {' '}sera considérée comme faite pour le cycle en cours : elle ne sera plus à relancer
            et ne reviendra qu'avec le planning de l'année suivante.
          </p>
        )}
      />
    </div>
  );
}

function NextActionsSection({
  actions, userName, companyUsers = [], teamActions = [], onMarkTeamActionDone,
}: NextActionsSectionProps) {
  if (actions.length === 0 && teamActions.length === 0) { return null; }

  const hasTeamActions = teamActions.length > 0;

  return (
    <div>
      {/* Section header */}
      <div className="mb-4 flex items-center justify-between rounded-[10px] bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-3.5 shadow-md">
        <div className="flex items-center gap-3">
          <span className="text-base font-extrabold text-white">
            Les prochaines actions à faire
          </span>
        </div>
        {userName && (
          <span className="text-xs font-bold whitespace-nowrap text-white/90">
            {userName}
          </span>
        )}
      </div>

      {/* Sub-section 1: personal actions */}
      {actions.length > 0 && (
        <div>
          {hasTeamActions && (
            <div className="mb-3 flex items-center gap-2">
              <PlayIcon className="h-4 w-4 shrink-0 text-rgpd-blue-dark" />
              <span className="text-[15px] font-extrabold text-slate-900">
                Les actions que vous devez faire
              </span>
            </div>
          )}
          <div className="flex flex-col gap-2">
            {actions.map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                companyUsers={companyUsers}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sub-section 2: team tracking (referent only) */}
      {hasTeamActions && (
        <TeamTrackingSubSection
          actions={teamActions}
          companyUsers={companyUsers}
          onMarkDone={onMarkTeamActionDone}
        />
      )}
    </div>
  );
}

export default NextActionsSection;
